import React from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { MediaCardContainer, AspectRatio } from "./MediaCardContainer";
import { MediaDisplay } from "./MediaDisplay";
import * as Overlay from "./MediaOverlay";
import * as Badge from "./MediaBadges";

interface ApprovalMediaCardProps {
  mediaItem: MediaItem;
  aspectRatio?: AspectRatio;
  isReel?: boolean;
  isCarousel?: boolean;
  carouselIndex?: number;
  carouselTotal?: number;
  showNumbering?: boolean;
  onClick?: () => void;
}

export const ApprovalMediaCard: React.FC<ApprovalMediaCardProps> = ({
  mediaItem,
  aspectRatio = "4:5",
  isReel = false,
  isCarousel = false,
  carouselIndex = 0,
  carouselTotal = 1,
  showNumbering = false,
  onClick,
}) => {
  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("[data-interactive]")) {
      return;
    }
    onClick?.();
  };

  return (
    <MediaCardContainer
      aspectRatio={aspectRatio}
      onClick={handleCardClick}
    >
      <MediaDisplay
        mediaItem={mediaItem}
        useThumbnail={true}
        showPlayButton={mediaItem.isVideo()}
      />

      {/* Top-right: Reel indicator */}
      {isReel && (
        <Overlay.TopRight>
          <Badge.ReelBadge />
        </Overlay.TopRight>
      )}

      {/* Bottom-right: Carousel indicator */}
      {isCarousel && carouselTotal > 1 && (
        <Overlay.BottomRight>
          <Badge.CarouselIndicator
            current={carouselIndex}
            total={carouselTotal}
          />
        </Overlay.BottomRight>
      )}

      {/* Bottom-left: Carousel dots (if not too many) */}
      {isCarousel && carouselTotal > 1 && carouselTotal <= 5 && (
        <Overlay.BottomLeft>
          <div className="flex gap-1">
            {Array.from({ length: carouselTotal }, (_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index === carouselIndex ? "bg-white" : "bg-white/50"
                }`}
              />
            ))}
          </div>
        </Overlay.BottomLeft>
      )}

      {/* Carousel numbering */}
      {showNumbering && isCarousel && (
        <Overlay.BottomLeft>
          <Badge.SelectionBadge index={carouselIndex} showNumber={true} />
        </Overlay.BottomLeft>
      )}
    </MediaCardContainer>
  );
};
